import React from 'react';
import { bool, oneOf, string } from 'prop-types';
import classNames from 'classnames';
import {
  AlarmClock,
  Archive,
  ArrowBigDown,
  ArrowBigUp,
  ArrowDown,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUp,
  ArrowUpDown,
  Atom,
  AtSign,
  Award,
  Ban,
  Bell,
  Bold,
  Bookmark,
  Calendar,
  ChartNoAxesColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleArrowRight,
  CircleCheck,
  CircleDollarSign,
  CircleDot,
  CirclePlus,
  CircleStar,
  CircleUserRound,
  Code,
  Copy,
  CreditCard,
  DoorOpen,
  Download,
  Drama,
  Ellipsis,
  ExternalLink,
  FileText,
  Flag,
  Forward,
  Gauge,
  Globe,
  Hash,
  Heart,
  Hourglass,
  House,
  IdCard,
  Image,
  ImageOff,
  Inbox,
  Info,
  Italic,
  KeyRound,
  Laptop,
  LayoutDashboard,
  LayoutGrid,
  Link,
  List,
  ListOrdered,
  LoaderCircle,
  Lock,
  Mail,
  MailCheck,
  MailWarning,
  MapPin,
  Maximize,
  Menu,
  MessageCircle,
  MessageSquare,
  MessagesSquare,
  Minimize,
  Minus,
  MousePointerClick,
  Navigation,
  PartyPopper,
  Pause,
  Pencil,
  Phone,
  Pin,
  Play,
  Plus,
  Repeat,
  Reply,
  RotateCcw,
  Search,
  Send,
  Settings,
  Share,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Smile,
  SmilePlus,
  Star,
  Strikethrough,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Underline,
  Undo2,
  Unlink,
  Upload,
  UserRound,
  UserRoundX,
  Users,
  Video,
  Volume2,
  VolumeX,
  Vote,
  WandSparkles,
  X,
  Zap,
} from 'lucide-react';

import css from './Icon.module.css';

/**
 * The app's icons, by what they mean. Each name points at one Lucide icon (lucide.dev, ISC
 * licence), imported on its own so only these reach the bundle. Add a name here before using a
 * new icon, and pick the Lucide icon closest to the board's drawing.
 */
export const ICONS = {
  add: Plus,
  addCircle: CirclePlus,
  addReaction: SmilePlus,
  archive: Archive,
  arrowDown: ArrowDown,
  arrowLeft: ArrowLeft,
  arrowRight: ArrowRight,
  arrowRightCircle: CircleArrowRight,
  arrowUp: ArrowUp,
  arrowUpDown: ArrowUpDown,
  atom: Atom,
  banned: Ban,
  bold: Bold,
  bookmark: Bookmark,
  boost: Zap,
  bulletList: List,
  calendar: Calendar,
  celebrate: PartyPopper,
  check: Check,
  chevronDown: ChevronDown,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  chevronUp: ChevronUp,
  chevronUpDown: ChevronsUpDown,
  circle: Circle,
  close: X,
  code: Code,
  comment: MessageCircle,
  comments: MessagesSquare,
  contact: IdCard,
  copy: Copy,
  credits: Award,
  cta: MousePointerClick,
  dashboard: LayoutDashboard,
  delete: Trash2,
  document: FileText,
  download: Download,
  edit: Pencil,
  emoji: Smile,
  error: CircleAlert,
  external: ExternalLink,
  forward: Forward,
  genre: Drama,
  globe: Globe,
  grid: LayoutGrid,
  hash: Hash,
  heart: Heart,
  home: House,
  image: Image,
  imageOff: ImageOff,
  inbox: Inbox,
  info: Info,
  insights: ChartNoAxesColumn,
  italic: Italic,
  key: KeyRound,
  keySuccess: ShieldCheck,
  laptop: Laptop,
  leave: DoorOpen,
  link: Link,
  list: List,
  loading: LoaderCircle,
  location: MapPin,
  lock: Lock,
  mail: Mail,
  mailCheck: MailCheck,
  mailWarning: MailWarning,
  maximize: Maximize,
  mention: AtSign,
  menu: Menu,
  message: MessageSquare,
  minimize: Minimize,
  minus: Minus,
  money: CircleDollarSign,
  more: Ellipsis,
  mute: VolumeX,
  myAttractr: CircleDot,
  navigation: Navigation,
  notifications: Bell,
  numberedList: ListOrdered,
  pause: Pause,
  payment: CreditCard,
  person: UserRound,
  phone: Phone,
  pin: Pin,
  play: Play,
  poll: Vote,
  preferences: SlidersHorizontal,
  premium: CircleStar,
  refresh: RotateCcw,
  repeat: Repeat,
  reply: Reply,
  report: Flag,
  restricted: UserRoundX,
  search: Search,
  security: Shield,
  send: Send,
  settings: Settings,
  share: Share,
  star: Star,
  strikethrough: Strikethrough,
  success: CircleCheck,
  swap: ArrowLeftRight,
  thumbsDown: ThumbsDown,
  thumbsUp: ThumbsUp,
  tier: Star,
  time: AlarmClock,
  timeLeft: Hourglass,
  trendDown: TrendingDown,
  trendUp: TrendingUp,
  underline: Underline,
  undo: Undo2,
  unlink: Unlink,
  upload: Upload,
  usage: Gauge,
  user: CircleUserRound,
  users: Users,
  video: Video,
  volume: Volume2,
  voteDown: ArrowBigDown,
  voteUp: ArrowBigUp,
  wand: WandSparkles,
  warning: TriangleAlert,
};

export const ICON_NAMES = Object.keys(ICONS);
// 12-24 for icons in text, buttons and bars; 32 and 48 only where an icon stands in for missing
// content (an empty upload area, a photo slot)
export const ICON_SIZES = [12, 14, 16, 18, 20, 24, 32, 48];

// The boards draw outline icons with a stroke of about 1.5px whatever the size (1.4–1.6 at
// 16–20px, 1.5–1.8 at 12px). `absoluteStrokeWidth` keeps it at 1.5px at every size.
const STROKE_WIDTH = 1.5;

/**
 * One icon from the app's set: an outline in the text colour (`currentColor`), so it follows the
 * colour of the text or button around it. Decorative by default (hidden from screen readers);
 * pass `title` when the icon is the only thing that says what it means. An icon inside a button
 * that has its own label stays decorative.
 *
 * `filled` fills the shape with the same colour, for "on" states (a given star or vote).
 * `solid` draws a circled icon (`success`, `error`) as a filled disc with a white mark.
 */
const Icon = props => {
  const { rootClassName, className, name, size, title, filled, solid } = props;
  const Glyph = ICONS[name];
  if (!Glyph) {
    return null;
  }
  const classes = classNames(css.icon, rootClassName || css.root, className, {
    [css.filled]: filled,
    [css.solid]: solid,
  });
  const a11yProps = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': 'true' };
  return (
    <Glyph
      className={classes}
      size={size}
      strokeWidth={STROKE_WIDTH}
      absoluteStrokeWidth
      focusable="false"
      {...a11yProps}
    />
  );
};

Icon.defaultProps = {
  rootClassName: null,
  className: null,
  size: 18,
  title: null,
  filled: false,
  solid: false,
};

Icon.propTypes = {
  rootClassName: string,
  className: string,
  name: oneOf(ICON_NAMES).isRequired,
  size: oneOf(ICON_SIZES),
  title: string,
  filled: bool,
  solid: bool,
};

export default Icon;
